<template>
  <div class="navigation">
    <div v-if="!showFooter && !isMobile" class="justify-self-end flex justify-end gap-5">
      <base-button
        data-test="prev"
        class="flex items-center transition duration-200 uppercase"
        :class="{ 'hover:bg-black hover:text-white': couldNavigatePrev }"
        :disabled="!couldNavigatePrev"
        @click="prevPage"
        >{{ textSnippets.back }}</base-button
      >

      <base-button
        v-if="isLastPage"
        class="flex items-center uppercase"
        @click="finish"
        type="primary"
        :bordered="false"
        :disabled="!isAnswered || checkingResult"
        data-test="result"
        >{{ textSnippets.show_result }}</base-button
      >
      <base-button
        data-test="next"
        v-else
        class="flex items-center uppercase"
        :disabled="!couldNavigateNext || !isAnswered"
        @click="nextPage"
        :bordered="false"
        type="primary"
        >{{ textSnippets.forward }}</base-button
      >
    </div>
    <!-- mobile footer -->
    <div v-if="!showFooter && isMobile" class="justify-self-end flex gap-5 justify-end">
      <base-button
        data-test="prev"
        class="flex items-center transition duration-200 uppercase"
        :class="{ 'hover:bg-black hover:text-white': couldNavigatePrev }"
        :disabled="!couldNavigatePrev"
        @click="prevPage"
        >{{ textSnippets.back }}</base-button
      >

      <base-button
        v-if="isLastPage"
        class="flex items-center uppercase"
        @click="finish"
        type="primary"
        :bordered="false"
        :disabled="!isAnswered || checkingResult"
        data-test="result"
        >{{ textSnippets.show_result }}</base-button
      >
      <base-button
        v-else
        data-test="next"
        class="flex items-center uppercase"
        :disabled="!couldNavigateNext || !isAnswered"
        type="primary"
        :bordered="false"
        @click="nextPage"
        >{{ textSnippets.forward }}</base-button
      >
    </div>
  </div>
</template>
<script lang="ts">
import { defineComponent, computed, inject, ref } from 'vue'
import { PhArrowLeft, PhArrowRight } from '@dnlsndr/vue-phosphor-icons'
import BaseButton from '@/components/base/BaseButton.vue'
import { useMainStore } from '@/store/main'
import { testStatus } from '@/types/test-status'
import { useRoute, useRouter } from 'vue-router'
import { AxiosResponse } from 'axios'
import { getNavigationConditions } from '@/composables/navigationConditions'

export default defineComponent({
  components: {
    PhArrowLeft,
    BaseButton,
    PhArrowRight,
  },
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
  },

  setup() {
    /**
     * register store
     */
    const store = useMainStore()

    /**
     * inject local config
     */
    const config: any = inject('config')

    /**
     * register router
     */
    const router = useRouter()

    /**
     * inject axios
     */
    const axios: any = inject('axios')
    /**
     * @return {Language} textSnippets
     */
    const textSnippets = computed(() => store.snippets)

    /**
     * Import template conditionns for navigation
     */
    const { showFooter, couldNavigatePrev, isLastPage, couldNavigateNext } = getNavigationConditions()

    /**
     * @returns {boolean} isAnswered
     * @description if no answer is set, stop next
     */
    const isAnswered = computed(() => store.answer.length > 0)
    /**
     * @return {Answers} answers
     */
    const answers = computed(() => store.answers)

    /**
     * @returns {number} page
     */
    const page = computed(() => store.page)

    /**
     * @description navigate to next page
     */
    const nextPage = () => {
      if (couldNavigateNext.value) {
        store.saveAnswer()
        const newPage = page.value + 1
        store.setPage(newPage)
        router.push({ name: 'start', params: { id: newPage } })
      }
    }
    /**
     * @description navigate to prev page
     */
    const prevPage = () => {
      if (couldNavigatePrev.value) {
        const newPage = page.value - 1
        store.setPage(newPage)
        router.push({ name: 'start', params: { id: newPage } })
      }
    }

    const checkingResult = ref(false)

    /**
     * @description save answers, set status and navigate to the next page
     * result page if exact
     * finish page if not
     */
    const finish = async () => {
      if (isLastPage.value) {
        checkingResult.value = true
        store.saveAnswer()
        // Set Store status to started
        store.setStatus(testStatus.Ended)
        const resultUuid = await sendProgress()
        // return if resultUuid is null or undefinded
        if (!resultUuid) return false
        store.setResultUuid(resultUuid)
        const result = await sendResult(resultUuid)
        if (result?.status === 200) {
          // set result data in store and redirect
          store.setResult(result.data)
          showResult()
        } else if (result?.status === 201) {
          router.push({ name: 'ambiguous' })
        } else {
          // redirect to finish page if result is not exact
          console.error(result)
        }
      }
    }
    /**
     * @description navigate to result page
     * set test status to checkout
     */

    const showResult = async () => {
      store.setStatus(testStatus.Checkout)
      router.push({ name: 'result' })
    }

    /**
     * @description send progress to server
     */
    const sendProgress = async (): Promise<string | null> => {
      try {
        const response = await axios.post(`${config.baseUrl}/api/v1/questionnaire/${config.testSlug}/progress`, {
          answers: answers.value,
          referrer: window.location.href,
        })
        return response.data.resultUuid
      } catch (error) {
        console.error(error)
        return null
      }
    }

    // send anweres and get result
    const sendResult = async (resultUuid: string): Promise<AxiosResponse | undefined> => {
      try {
        return await axios.post(`${config.baseUrl}/api/v1/questionnaire/${config.testSlug}/result`, {
          answers: answers.value,
          resultUuid,
        })
      } catch (error) {
        console.error(error)
      }
    }

    return {
      textSnippets,
      finish,
      nextPage,
      prevPage,
      isAnswered,
      showFooter,
      couldNavigateNext,
      couldNavigatePrev,
      isLastPage,
      checkingResult,
    }
  },
})
</script>
